import React, { useContext, useState } from 'react'
import { AppContext } from 'contexts/AppContext';
import { Box, useMediaQuery } from '@mui/material';
import { Modal } from 'react-bootstrap';
import "./Order.css"
import { translate } from 'utils/Translate';
import ExplainerText from 'components/elements/ExplainerText';
import Headline6 from 'components/elements/Headline6';
import Image from 'components/elements/Image';
import { useNavigate } from 'react-router-dom';
import { getTranslatedProductTitle, newLineToBreakTag, priceConvertor } from 'utils/general';
import { callAPI } from 'utils/API';
import IconButtonMedium from 'components/elements/IconButtonMedium';
import Close from 'components/icons/Close';
import ContentLarge from 'components/elements/ContentLarge';
import ButtonMedium3 from 'components/elements/ButtonMedium3';
import ButtonMedium1 from 'components/elements/ButtonMedium1';
import ContentNormal from 'components/elements/ContentNormal';
import Headline4 from 'components/elements/Headline4';
import { Check } from '@mui/icons-material';
import moment from 'moment';
import ComputerCheck from 'components/icons/ComputerCheck';
import ClipboardCross from 'components/icons/ClipboardCross';
import ClipboardCheck from 'components/icons/ClipboardCheck';
import PackageThumbs from 'components/icons/PackageThumbs';
import ShippingTruck from 'components/icons/ShippingTruck';
import ShippingTruckCheck from 'components/icons/ShippingTruckCheck';
import Headline5 from 'components/elements/Headline5';

const Order = (props) => {
    const { styleGuide, brandData } = useContext(AppContext)
    const { order, refresh, index } = props
    const [showConfirmation, setShowConfirmation] = useState(false)
    const [showResult, setShowResult] = useState(false)
    const isMobile = useMediaQuery('(max-width:900px)')

    const showCancel = brandData?.features?.showCancelSeasonOrder && order?.orderType?.toUpperCase() === "SEASON" && ['NEW','TENTATIVE'].includes(order?.orderFlowStatus?.toUpperCase())

    const cancelOrder = async (event) => {
        event.stopPropagation()
        setShowConfirmation(false)
        if(!order?.OrderID) return 
        const cancelResult = await callAPI("order/cancel", "POST", { body: { OrderID: order.OrderID }})
        if(cancelResult?.affectedRows || cancelResult?.changedRows) {
            refresh()
            setShowResult(true)
            setTimeout(()=>{
                setShowResult(false)
            }, 5000)
        }
    }

    const OrderHeadlineComponent = isMobile ? Headline5 : Headline4
    const orderPrice = brandData?.isMerchandise ? order.amountCurrency : order.amountPoints

    return !order ? null : <>
        <Box position={'relative'} borderBottom={`1px solid ${styleGuide.color4}`} padding={isMobile ? 0 : 4}>
            <Box display={'flex'} justifyContent={'space-between'} flex={1} alignItems={'center'} flexWrap={'wrap'} gap={2} padding={2}>
                <Box display={'flex'}>
                    <OrderHeadlineComponent translationKey="_PROFILE_ORDER_PART_" fallbackText={`Ordredel`} />
                    <OrderHeadlineComponent fallbackText={`: ${index}`} />
                </Box>
                <Box display={'flex'} gap={2} alignItems={'center'}>
                    {!brandData?.features?.disablePoints &&
                        <Box display={'flex'} gap={1} alignItems={'center'}>
                            {brandData?.isMerchandise
                                ? <ContentNormal translationKey='_PROFILE_ORDER_TOTAL_AMOUNT_CURRENCY_' fallbackText={'Samlet beløb'} />
                                : <ContentNormal translationKey='_PROFILE_ORDER_TOTAL_AMOUNT_POINTS_' fallbackText={'Samlet antal point'} />
                            }
                            <Headline6 fallbackText={`${priceConvertor(brandData, (orderPrice), ' ')}`} />
                        </Box>
                    }
                    {!!showCancel &&
                        <Box className='order-status' display={'flex'} flexDirection={'column'} alignItems={'center'} sx={{float: 'right'}}>
                            <IconButtonMedium
                                title={translate('_ORDERLIST_ORDER_CANCEL_CAPTION_', 'Cancel order')}
                                icon={<Close />}
                                onClick={(event) => {
                                    event.stopPropagation()
                                    setShowConfirmation(true)
                                }}
                            />
                        </Box>
                    }
                </Box>
            </Box>
            <Box display={'flex'} flexDirection={'column'} padding={2} gap={4}>
                {order.orderRemark && (
                    <Box display={'flex'} gap={1} flexWrap={'wrap'} alignItems={'center'}>
                        <Headline6 translationKey="_REMARK_" fallbackText='Bemærkning: ' />
                        {newLineToBreakTag(order.orderRemark)}
                    </Box>
                )}
                <Box display={'flex'} flexWrap={'wrap'} gap={4}>
                    { order.orderLines.map(orderline => <OrderItem key={orderline.OrderlineID} orderLine={orderline} />) }
                </Box>
            </Box>
            <Box marginTop={2} padding={1}>
                <OrderProgress order={order} timelineEvents={props.timelineEvents} />
            </Box>
        </Box>
        <Modal
            show={showResult}
            onHide={() => setShowResult(false)}
            centered
        >
            <Modal.Header
                className="pb-0 border-0"
                closeButton
                style={{ fontSize: 9 }}
            />
            <Modal.Body className="pt-0">
                <ContentLarge fallbackText="Order cancelled" translationKey="_ORDER_CANCEL_SUCCESS_" />
            </Modal.Body>
        </Modal>
        <Modal
            show={showConfirmation}
            onHide={() => setShowConfirmation(false)}
            centered
        >
            <Modal.Header
                className="pb-0 border-0"
                closeButton
                style={{ fontSize: 9 }}
            />
            <Modal.Body className="pt-0">
                <ContentNormal fallbackText="Confirm cancellation" translationKey="_ORDER_CANCEL_CONFIRM_CAPTION_" />
            </Modal.Body>
            <Modal.Footer>
                <ButtonMedium1 showIcon={false} translationKey="_CONFIRM_" fallbackText="Bekræft" onClick={cancelOrder} />
                <ButtonMedium3 showIcon={false} translationKey="_BUTTON_CAPTION_CLOSE_" fallbackText="Luk" onClick={(event) => {
                    event.stopPropagation()
                    setShowConfirmation(false)
                }} />
            </Modal.Footer>
        </Modal>
    </>
}

const OrderItem = (props) => {
    const navigate = useNavigate()
    const { styleGuide, brandData, langCode } = useContext(AppContext)
    const { orderLine } = props
    const isMobile = useMediaQuery('(max-width:900px)')
    const pointSuffix = translate("_POINTS_SHORT_", "pt")
    const dkkSuffix = translate("_DKK_", "kr")
    const productTitle = getTranslatedProductTitle(orderLine, langCode)
    const goToProduct = () => navigate(`/product/${orderLine.ProductID}`)

    const orderItemPrice = brandData?.isMerchandise ? orderLine.amountCurrency : orderLine.amountPoints

    return (
        <Box display={'flex'} gap={isMobile ? 2 : 3} width={350}>
            <Box display={'flex'} gap={2} sx={{cursor: 'pointer'}}>
                <Image
                    style={{
                        height: 175,
                        width: 140,
                        borderWidth: 0.5,
                        borderStyle: 'solid',
                        borderColor: styleGuide.color2,
                     }}
                    src={`url(${orderLine.imageUrl})`}
                    onClick={goToProduct}
                />
            </Box>

            <Box display={'flex'} flexDirection={'column'} gap={1} justifyContent={'space-between'} flex={1}>
                <Box style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 4 : 10 }}>
                    <Headline6 fallbackText={orderLine.manufacturer || ' '} />
                    <ExplainerText onClick={goToProduct} fallbackText={productTitle || orderLine.orderlineTitle} style={{cursor: 'pointer'}} />
                    <Box style={{ display: 'flex', alignItems: 'center' }}>
                        {((orderLine.variationColor && orderLine.variationSize) || orderLine.variationTitle) &&
                            <>
                                <ExplainerText
                                    fallbackText={orderLine.variationSize ? `${orderLine.variationSize}` : `${orderLine.variationTitle}`}
                                />
                                {orderLine.variationColor
                                    ? <ExplainerText fallbackText={`, ${orderLine.variationColor}`} />
                                    : null
                                }
                            </>
                        }
                    </Box>
                    
                </Box>
                <Box display={'flex'} justifyContent={'space-between'}>
                    <ExplainerText fallbackText={
                        <>
                            {translate('_QUANTITY_', 'Antal')}
                            <span>: {orderLine.quantity || '-'}</span>
                        </>
                    }/>
                    <ExplainerText fallbackText={`${ priceConvertor(brandData, orderItemPrice, brandData.isMerchandise ? dkkSuffix : pointSuffix)}`} />
                </Box>
            </Box>
        </Box>
    )
}

const OrderProgress = (props) => {
    const { order, timelineEvents } = props
    const { styleGuide } = useContext(AppContext)
    const isMobile = useMediaQuery('(max-width:1200px)')

    if(order.orderFlowStatus === 'DECLINED' || order.orderFlowStatus === 'REFUNDED'){
        const declinedEvent = timelineEvents.find(e => e.timelineEvent === order.orderFlowStatus)
        const declinedDate = declinedEvent ? moment(declinedEvent.timeInsert) : undefined

        return (
            <Box display={'flex'} flexDirection={isMobile ? 'column' : 'row'} gap={isMobile ? 6 : undefined} marginBottom={2} padding={2} >
                <OrderProgressItem isMobile={isMobile} order={order} status={'NEW'} date={order.date} dotColor={styleGuide.color12} textColor={styleGuide.color7}/>
                <OrderProgressItem isMobile={isMobile} order={order} status={order.orderFlowStatus} date={declinedDate} dotColor={styleGuide.color12} textColor={styleGuide.color12}/>
            </Box>
        )
    }

    const orderedEvent = timelineEvents.find(e => e.timelineEvent === 'ORDERED')
    const orderedDate = orderedEvent ? moment(orderedEvent.timeInsert) : undefined

    const packedEvent = timelineEvents.find(e => e.timelineEvent === 'PACKED')
    const packedDate = packedEvent ? moment(packedEvent.timeInsert) : undefined

    const shippedEvent = timelineEvents.find(e => e.timelineEvent === 'SHIPPED')
    const shippedDate = order.overrideShipmentDate
        ? moment(order.overrideShipmentDate)
        : shippedEvent
            ? moment(shippedEvent.timeInsert)
            : undefined

    if(order.orderType === "SEASON") {
        return (
            <Box display={'flex'} flexDirection={isMobile ? 'column' : 'row'} paddingX={isMobile ? 2 : 0} paddingY={2} gap={isMobile ? 6 : undefined} marginBottom={isMobile ? 2 : undefined}>
                <OrderProgressItem isMobile={isMobile} order={order} status={'NEW'} date={order.date} />
                <OrderProgressItem isMobile={isMobile} order={order} status={'SHIPPED'} date={shippedDate} />
            </Box>
        )
    }

    return (
        <Box display={'flex'} flexDirection={isMobile ? 'column' : 'row'} paddingX={isMobile ? 2 : 0} paddingY={2} gap={isMobile ? 6 : undefined} marginBottom={isMobile ? 2 : undefined}>
            <OrderProgressItem isMobile={isMobile} order={order} status={'NEW'} date={order.date} />
            <OrderProgressItem isMobile={isMobile} order={order} status={'ORDERED'} date={orderedDate} />
            <OrderProgressItem isMobile={isMobile} order={order} status={'PACKED'} date={packedDate} />
            <OrderProgressItem isMobile={isMobile} order={order} status={'SHIPPED'} date={shippedDate} />
            {(order.orderFlowStatus !== 'SHIPPED' || (props.order.trackingUrl && props.order.trackingNumber)) &&
                <OrderProgressItem isMobile={isMobile} order={order} status={'RECEIVED'} />
            }
        </Box>
    )
}

const OrderProgressItem = (props) => {
    const { styleGuide } = useContext(AppContext)

    if(!props.order){
        return null
    }

    const statusToIcon = (color) => {
        switch (props.status) {
            case 'NEW':
                return <ComputerCheck stroke={color} />
            case 'ORDERED':
                return <ClipboardCheck stroke={color} />
            case 'PACKED':
                return <PackageThumbs stroke={color} />
            case 'SHIPPED':
                return <ShippingTruck stroke={color} />
            case 'RECEIVED':
                return <ShippingTruckCheck stroke={color} />
            case 'DECLINED':
            case 'REFUNDED':
                return <ClipboardCross stroke={color} />
        }
    }

    const statusToLabel = () => {
        switch (props.status) {
            case 'NEW':
                return translate('_ORDER_STATUS_NEW_', 'Ordre placeret')
            case 'ORDERED':
                return translate('_ORDER_STATUS_ORDERED_', 'Bestilt')
            case 'PACKED':
                return translate('_ORDER_STATUS_PACKED_', 'Klargøres')
            case 'SHIPPED':
                return (props.order.orderFlowStatus !== 'SHIPPED' || (props.order.trackingUrl && props.order.trackingNumber)) ? translate('_ORDER_STATUS_SHIPPED_', 'Afsendt') : translate('_ORDER_STATUS_SHIPPED_DROPSHIP_', 'Sendes af leverandør')
            case 'RECEIVED':
                return translate('_ORDER_STATUS_RECEIVED_', 'Leveret')
            case 'DECLINED':
                return translate('_ORDER_STATUS_DECLINED_', 'Afvist')
            case 'REFUNDED':
                return translate('_ORDER_STATUS_REFUNDED_', 'Refunderet')
            default:
                return ''
        }
    }

    const stepCompleted = () => {
        switch (props.status) {
            case 'ORDERED':
                return ['ORDERED', 'PACKED', 'SHIPPED', 'RECEIVED'].some(s => s === props.order.orderFlowStatus)
            case 'PACKED':
                return ['PACKED', 'SHIPPED', 'RECEIVED'].some(s => s === props.order.orderFlowStatus)
            case 'SHIPPED':
                return props.order.orderFlowStatus === 'SHIPPED' || (props.order.orderFlowStatus === 'RECEIVED' && props.order.orderType !== "SEASON")
            case 'RECEIVED':
                return ['RECEIVED'].some(s => s === props.order.orderFlowStatus)
            default:
                return true
        }
    }

    const completed = stepCompleted()
    const dotColor = props.dotColor || 'black'
    const textColor = props.textColor || 'black'

    const statusInfo = () => {
        if(props.status === 'SHIPPED'){
            if(props.order.trackingUrl && props.order.trackingNumber){
                return (
                    <a href={`${props.order.trackingUrl}${props.order.trackingNumber}`} target='_blank' rel='noreferrer' style={{ textDecoration: 'none' }}>
                        <ContentNormal style={{ color: styleGuide.color16 }} translationKey='_ORDERLIST_ORDER_TRACK_LINK_LABEL_' fallbackText={'Track & trace'} />
                    </a>
                )
            } else {
                return (
                    <ContentNormal>
                        Dropship
                    </ContentNormal>
                )
            }
        } else if(props.status === 'RECEIVED') {
            if(props.order.trackingUrl && props.order.trackingNumber){
                return (
                    <a href={`${props.order.trackingUrl}${props.order.trackingNumber}`} target='_blank' rel='noreferrer' style={{ textDecoration: 'none' }}>
                        <ContentNormal style={{ color: styleGuide.color16 }} translationKey='_ORDERLIST_ORDER_DELIVERY_LABEL_' fallbackText={'Se leveringsinfo'} />
                    </a>
                )
            }
        }

        return (
            <ContentNormal style={{ color: textColor }}>
                {`Kl: ${props.date.format("HH:mm")}`}
            </ContentNormal>
        )
    }

    if(props.isMobile) {
        return (
            <Box flex={props.status !== 'NEW' ? 1 : undefined} display={'flex'} flexDirection={'column'} style={{ opacity: completed ? 1 : 0.2 }} gap={2} paddingLeft={1} position={'relative'} >
                <Box bgcolor={dotColor} width={4} position={'absolute'} top={-24} bottom={-24} left={62} zIndex={1} />
                <Box position={'relative'} display={'flex'} alignItems={'center'} gap={2}>
                    <Box width={90} height={90} margin={-4}>
                        {statusToIcon(textColor)}
                    </Box>

                    <Box display={'flex'} flex={1} flexWrap={'wrap'} marginLeft={4} gap={1}>
                        <ContentNormal style={{ color: textColor, fontWeight: 600 }}>
                            {statusToLabel()}
                        </ContentNormal>

                        {completed && props.date &&
                            <Box display={'flex'} gap={1}>
                                {(props.status !== 'SHIPPED' || (props.order.trackingUrl && props.order.trackingNumber)) &&
                                    <ContentNormal style={{ color: textColor }}>
                                        {props.date.format("DD/MM-YYYY")}
                                    </ContentNormal>
                                }
                                {statusInfo()}
                            </Box>
                        }
                        {!completed && props.status === 'SHIPPED' && !!props.order.expectedShipmentDate &&
                            <Box display={'flex'} gap={1}>
                                <ContentNormal style={{ color: textColor }}>
                                    {moment(props.order.expectedShipmentDate).format("DD/MM-YYYY")}
                                </ContentNormal>
                                <ContentNormal style={{ color: textColor }}
                                    translationKey={'_ORDERLIST_ORDER_EXPECTED_SHIPMENT_LABEL_'}
                                    fallbackText={'Forventet afsendelse'}
                                />
                            </Box>
                        }
                    </Box>
                </Box>
            </Box>
        )
    }

    return (
        <Box flex={props.status === 'NEW' ? undefined : 1} display={'flex'} style={{opacity: completed ? 1 : 0.2}} maxWidth={356}>
            {/* Connector */}
            {props.status !== 'NEW' &&
                <Box position={'relative'} flex={1}>
                    <Box bgcolor={dotColor} height={4} position={'absolute'} left={-81} right={-81} top={93} zIndex={1} />
                </Box>
            }
            <Box position={'relative'} display={'flex'} flexDirection={'column'} alignItems={'center'} gap={1} width={props.status === 'NEW' ? 150 : 190}>
                <Box width={90} height={90} margin={-3}>
                    {statusToIcon(textColor)}
                </Box>
                <ContentNormal style={{color: textColor}}>
                    {statusToLabel()}
                </ContentNormal>
                <Box borderRadius={'100%'} bgcolor={dotColor} zIndex={2} display={'flex'} justifyContent={'center'} padding={0.5} style={{aspectRatio: 1}}>
                    <Check fontSize='small' style={{color: 'white'}} />
                </Box>
                {completed && props.date &&
                    <Box display={'flex'} flexDirection={'column'} alignItems={'center'}>
                        {(props.status !== 'SHIPPED' || (props.order.trackingUrl && props.order.trackingNumber)) &&
                            <ContentNormal style={{color: textColor}}>
                                {props.date.format("DD/MM-YYYY")}
                            </ContentNormal>
                        }
                        {statusInfo()}
                    </Box>
                }
                {!completed && props.status === 'SHIPPED' && !!props.order.expectedShipmentDate &&
                    <Box display={'flex'} flexDirection={'column'} alignItems={'center'}>
                        <ContentNormal style={{color: textColor}}>
                            {moment(props.order.expectedShipmentDate).format("DD/MM-YYYY")}
                        </ContentNormal>
                        <ContentNormal style={{color: textColor}}
                            translationKey={'_ORDERLIST_ORDER_EXPECTED_SHIPMENT_LABEL_'}
                            fallbackText={'Forventet afsendelse'}
                        />
                    </Box>
                }
            </Box>
        </Box>
    )
}

export default Order;